import {
  STAGE_LABEL,
  isProcessingEstimate,
  formatTimeLeft,
  timeLeftHeadline,
  type EstimateStage,
  type ProcessingEstimate,
} from '../lib/processing-estimate';

const n = (v: number) => v.toLocaleString('en-US');

/**
 * The PROCESSING PROGRESS panel (owner ask 2026-10-23), shared by the admin's and
 * the seller's Processing pages: while work remains, "About N minutes left" and a
 * row per stage — Filing / Reading scans / Redaction — with documents done of
 * total, a progress bar and the stage's own time left; when everything is done,
 * "All documents processed" and how long it took. Every time is an estimate and
 * says so. Presentational: the page polls the estimate and hands it in.
 *
 * `waitingOnPeople`: documents the machines are finished with that now wait for a
 * person (filing or redaction review). Then "all processed" would be untrue, so
 * the done line says the automatic part is finished and who it is waiting on.
 */
export function ProcessingProgress({
  estimate,
  waitingOnPeople = 0,
}: {
  estimate: ProcessingEstimate | undefined;
  waitingOnPeople?: number;
}) {
  if (!isProcessingEstimate(estimate) || estimate.total === 0) return null;
  if (estimate.documentsLeft === 0) {
    const took = formatTimeLeft(estimate.tookSeconds);
    const waiting = waitingOnPeople > 0;
    return (
      <section
        aria-label="Processing progress"
        data-testid="processing-progress"
        className="rounded-lg border border-st-filed-line bg-st-filed p-3 text-sm text-st-filed-fg"
      >
        <p role="status" className="font-medium" data-testid="processing-progress-done">
          {waiting
            ? `Automatic processing is finished — ${n(waitingOnPeople)} ${waitingOnPeople === 1 ? 'document is' : 'documents are'} waiting on you`
            : 'All documents processed'}
          {took ? ` (took ${took})` : ''}
        </p>
      </section>
    );
  }
  const headline = timeLeftHeadline(estimate.secondsLeft ?? 1);
  return (
    <section
      aria-label="Processing progress"
      data-testid="processing-progress"
      className="space-y-3 rounded-lg border bg-muted/40 p-3"
    >
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <p className="text-sm font-semibold" data-testid="processing-progress-headline">
          {headline}
        </p>
        <p className="text-xs text-ink-3">An estimate — it updates as documents finish.</p>
      </div>
      <ul className="space-y-2.5" aria-label="Processing stages">
        {estimate.stages.map((s) => (
          <StageRow key={s.key} stage={s} />
        ))}
      </ul>
    </section>
  );
}

function StageRow({ stage }: { stage: EstimateStage }) {
  const label = STAGE_LABEL[stage.key];
  const percent = stage.total > 0 ? Math.round((stage.done / stage.total) * 100) : 100;
  const finished = stage.remaining === 0;
  const left = finished ? null : formatTimeLeft(stage.secondsLeft ?? 1);
  return (
    <li className="space-y-1" data-testid={`processing-stage-${stage.key}`}>
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 text-sm">
        <span className="font-medium">{label}</span>
        <span className="tabular-nums text-ink-2">
          {n(stage.done)} of {n(stage.total)}
          <span className="text-ink-3">
            {' · '}
            {finished ? 'done' : `${left} left`}
          </span>
        </span>
      </div>
      <div
        className="h-1.5 w-full overflow-hidden rounded-full bg-muted"
        role="progressbar"
        aria-label={`${label}: ${stage.done} of ${stage.total} documents`}
        aria-valuenow={percent}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <div
          className="h-full rounded-full bg-primary transition-[width] duration-500"
          style={{ width: `${percent}%` }}
        />
      </div>
    </li>
  );
}
